// Developer Platform — Tab 1: OAuth API clients (auth-service /developer/*).
// Create/list clients, edit scopes, rotate secrets (24h overlap), enable/
// disable. Secrets are shown exactly ONCE via the shared SecretModal.
import React, { useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage, isEntitlementUnavailable } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2, Pencil, Plus, Power, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { TruncatedText } from '@/features/shared/components/ui/TruncatedText';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import {
  createApiClient, getScopeCatalog, listApiClients, listApiScopes, rotateApiClientSecret,
  setApiClientEnabled, updateApiClientScopes, type ApiClient,
} from '@/services/developerService';
import { CopyButton, FeatureNotEnabledState, RolloutPendingState, SecretModal, errMsg, fmtDateTime, inputCls, TemporarilyUnavailableState } from './common';
import { isNotEntitled, isNotRolledOut } from '@/services/developerService';

/** Fallback scope catalog (used until GET /developer/scopes answers). */
const FALLBACK_SCOPES = [
  'employees:read', 'leave:read', 'payroll:read', 'reports:read',
  'webhooks:manage', 'integrations:manage',
];

const useScopeDescriptions = (): Record<string, string> => {
  const { t } = useTranslation('admin');
  return useMemo(() => ({
    'employees:read': t('developer.scope_employees_read', { defaultValue: 'Read the employee directory (basic profile fields only — no IDs, bank or salary data).' }),
    'leave:read': t('developer.scope_leave_read', { defaultValue: 'Read leave requests and leave balances.' }),
    'payroll:read': t('developer.scope_payroll_read', { defaultValue: 'Read payroll run summaries. Sensitive — grant only when required.' }),
    'reports:read': t('developer.scope_reports_read', { defaultValue: 'Download generated report exports.' }),
    'webhooks:manage': t('developer.scope_webhooks_manage', { defaultValue: 'Create and manage webhook subscriptions via the API.' }),
    'integrations:manage': t('developer.scope_integrations_manage', { defaultValue: 'Manage integration connections via the API.' }),
  }), [t]);
};

// ------------------------------------------------------- scope checkbox list
// M2: `sensitiveSet` marks scopes flagged sensitive in the catalog; `newlySensitive` are the ones
// this grant is ADDING (a re-save of an already-held sensitive scope isn't re-prompted). When any
// are present, an explicit acknowledgement is required and passed to the backend.
const ScopePicker: React.FC<{
  scopes: string[];
  selected: string[];
  onToggle: (scope: string) => void;
  sensitiveSet: Set<string>;
  newlySensitive: string[];
  ack: boolean;
  setAck: (v: boolean) => void;
}> = ({ scopes, selected, onToggle, sensitiveSet, newlySensitive, ack, setAck }) => {
  const { t } = useTranslation('admin');
  const descriptions = useScopeDescriptions();
  return (
    <div className="space-y-1.5">
      {scopes.map((scope) => (
        <label key={scope} className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-muted/40">
          <input
            type="checkbox"
            className="w-4 h-4 mt-0.5 accent-orange-500 shrink-0"
            checked={selected.includes(scope)}
            onChange={() => onToggle(scope)}
          />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-foreground font-mono">{scope}</span>
              {sensitiveSet.has(scope) && (
                <span className="text-[10px] font-medium rounded-full px-1.5 py-0.5 bg-warning-soft text-warning-strong border border-warning">
                  {t('developer.sensitive', { defaultValue: 'sensitive' })}
                </span>
              )}
            </span>
            <span className="block text-xs text-muted-foreground">
              {descriptions[scope] || t('developer.scope_generic', { defaultValue: 'Access governed by this scope.' })}
            </span>
          </span>
        </label>
      ))}
      {newlySensitive.length > 0 && (
        <label className="flex items-start gap-2.5 rounded-lg px-3 py-2 text-sm border bg-warning-soft border-warning text-warning-strong cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 mt-0.5 accent-amber-600 shrink-0"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
          />
          <span>
            {t('developer.sensitive_ack', {
              defaultValue: 'I understand these scopes grant sensitive access ({{scopes}}) to whoever holds this client’s credentials.',
              scopes: newlySensitive.join(', '),
            })}
          </span>
        </label>
      )}
    </div>
  );
};

// ------------------------------------------------------------- create dialog
const CreateClientDialog: React.FC<{
  scopes: string[];
  sensitiveSet: Set<string>;
  onCreated: (client: ApiClient, secret: string) => void;
  onClose: () => void;
}> = ({ scopes, sensitiveSet, onCreated, onClose }) => {
  const { t } = useTranslation('admin');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [environment, setEnvironment] = useState('live');
  // Actor kind (Agent Platform): server-assigned at creation; drives audit
  // attribution + the MCP tab's agent-connection listing.
  const [kind, setKind] = useState('app');
  const [selected, setSelected] = useState<string[]>([]);
  const [sensitiveAck, setSensitiveAck] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggle = (scope: string) =>
    setSelected((cur) => (cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]));

  const newlySensitive = selected.filter((s) => sensitiveSet.has(s));

  const submit = async () => {
    if (!name.trim()) {
      toast.error(t('developer.client_name_required', { defaultValue: 'Give the client a name.' }));
      return;
    }
    if (selected.length === 0) {
      toast.error(t('developer.scopes_required', { defaultValue: 'Select at least one scope.' }));
      return;
    }
    if (newlySensitive.length > 0 && !sensitiveAck) {
      toast.error(t('developer.sensitive_ack_required', { defaultValue: 'Acknowledge the sensitive scopes before continuing.' }));
      return;
    }
    setSaving(true);
    try {
      const { client, clientSecret } = await createApiClient({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        scopes: selected,
        environment,
        kind,
        acknowledgeSensitive: newlySensitive.length > 0 ? sensitiveAck : undefined,
      });
      onCreated(client, clientSecret);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.client_create_failed', { defaultValue: 'Could not create the API client.' }))));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('developer.new_client', { defaultValue: 'New API client' })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="dev-client-name">
                {t('developer.client_name', { defaultValue: 'Name' })}
              </label>
              <input
                id="dev-client-name"
                className={inputCls}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('developer.client_name_ph', { defaultValue: 'e.g. Accounting sync' })}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="dev-client-env">
                {t('developer.environment', { defaultValue: 'Environment' })}
              </label>
              <select
                id="dev-client-env"
                className={inputCls}
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
              >
                <option value="live">{t('developer.env_live', { defaultValue: 'Live' })}</option>
                <option value="sandbox">{t('developer.env_sandbox', { defaultValue: 'Sandbox' })}</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="dev-client-kind">
                {t('developer.client_kind', { defaultValue: 'Kind' })}
              </label>
              <select
                id="dev-client-kind"
                className={inputCls}
                value={kind}
                onChange={(e) => setKind(e.target.value)}
              >
                <option value="app">{t('developer.kind_app', { defaultValue: 'Application / integration' })}</option>
                <option value="agent">{t('developer.kind_agent', { defaultValue: 'AI agent (MCP)' })}</option>
                <option value="cli">{t('developer.kind_cli', { defaultValue: 'CLI / scripts' })}</option>
              </select>
              <p className="text-[11px] text-muted-foreground">
                {t('developer.kind_hint', { defaultValue: 'Recorded on every audit entry this client produces.' })}
              </p>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="dev-client-desc">
              {t('developer.client_desc', { defaultValue: 'Description (optional)' })}
            </label>
            <input
              id="dev-client-desc"
              className={inputCls}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('developer.client_desc_ph', { defaultValue: 'What will this client be used for?' })}
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">
              {t('developer.scopes', { defaultValue: 'Scopes' })}
            </p>
            <ScopePicker
              scopes={scopes}
              selected={selected}
              onToggle={toggle}
              sensitiveSet={sensitiveSet}
              newlySensitive={newlySensitive}
              ack={sensitiveAck}
              setAck={setSensitiveAck}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('developer.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {t('developer.create_client', { defaultValue: 'Create client' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -------------------------------------------------------- edit scopes dialog
const EditScopesDialog: React.FC<{
  client: ApiClient;
  scopes: string[];
  sensitiveSet: Set<string>;
  onSaved: (scopes: string[]) => void;
  onClose: () => void;
}> = ({ client, scopes, sensitiveSet, onSaved, onClose }) => {
  const { t } = useTranslation('admin');
  const [selected, setSelected] = useState<string[]>(client.scopes || []);
  const [sensitiveAck, setSensitiveAck] = useState(false);
  const [saving, setSaving] = useState(false);

  // The amber ack applies to sensitive scopes being NEWLY granted (already-held ones don't re-prompt).
  const alreadyGranted = new Set(client.scopes || []);
  const newlySensitive = selected.filter((s) => sensitiveSet.has(s) && !alreadyGranted.has(s));

  const toggle = (scope: string) =>
    setSelected((cur) => (cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]));

  const submit = async () => {
    if (selected.length === 0) {
      toast.error(t('developer.scopes_required', { defaultValue: 'Select at least one scope.' }));
      return;
    }
    if (newlySensitive.length > 0 && !sensitiveAck) {
      toast.error(t('developer.sensitive_ack_required', { defaultValue: 'Acknowledge the sensitive scopes before continuing.' }));
      return;
    }
    setSaving(true);
    try {
      await updateApiClientScopes(client.id, selected, newlySensitive.length > 0 ? sensitiveAck : undefined);
      toast.success(t('developer.scopes_saved', { defaultValue: 'Scopes updated.' }));
      onSaved(selected);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.scopes_save_failed', { defaultValue: 'Could not update scopes.' }))));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('developer.edit_scopes_title', { defaultValue: 'Scopes — {{name}}', name: client.name })}
          </DialogTitle>
        </DialogHeader>
        <ScopePicker
          scopes={scopes}
          selected={selected}
          onToggle={toggle}
          sensitiveSet={sensitiveSet}
          newlySensitive={newlySensitive}
          ack={sensitiveAck}
          setAck={setSensitiveAck}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('developer.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {t('developer.save_scopes', { defaultValue: 'Save scopes' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --------------------------------------------------------------------- tab
export const ApiClientsTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();

  const [clients, setClients] = useState<ApiClient[]>([]);
  const [scopes, setScopes] = useState<string[]>(FALLBACK_SCOPES);
  const [sensitiveSet, setSensitiveSet] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rolloutPending, setRolloutPending] = useState(false);
  const [notEnabledMsg, setNotEnabledMsg] = useState<string | null>(null);
  const [unavailableMsg, setUnavailableMsg] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ApiClient | null>(null);
  const [secret, setSecret] = useState<{ title: string; description: string; value: string } | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      listApiClients(),
      listApiScopes().catch(() => FALLBACK_SCOPES),
      getScopeCatalog().catch(() => []),
    ])
      .then(([list, scopeList, catalog]) => {
        if (!alive) return;
        setClients(list);
        if (scopeList.length) setScopes(scopeList);
        const s = new Set<string>();
        catalog.forEach((c) => c.scopes.forEach((x) => x.sensitive && s.add(x.scope)));
        setSensitiveSet(s);
      })
      .catch((e) => {
        if (!alive) return;
        if (isEntitlementUnavailable(e)) setUnavailableMsg(errMsg(e, ''));
        else if (isNotEntitled(e)) setNotEnabledMsg(errMsg(e, ''));
        else if (isNotRolledOut(e)) setRolloutPending(true);
        else setLoadError(true);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const onCreated = (client: ApiClient, clientSecret: string) => {
    setClients((cur) => [client, ...cur]);
    setCreating(false);
    setSecret({
      title: t('developer.client_secret_title', { defaultValue: 'Client secret — {{name}}', name: client.name }),
      description: t('developer.client_secret_desc', {
        defaultValue: 'Use it with the client ID to request access tokens (OAuth client-credentials).',
      }),
      value: clientSecret,
    });
  };

  const onRotate = async (client: ApiClient) => {
    const ok = await confirm({
      title: t('developer.rotate_title', { defaultValue: 'Rotate client secret' }),
      message: t('developer.rotate_confirm', {
        defaultValue: 'A new secret is issued immediately. The current secret keeps working for 24 hours to let you switch over, then it stops. Continue?',
      }),
      confirmLabel: t('developer.rotate', { defaultValue: 'Rotate secret' }),
    });
    if (!ok) return;
    setBusyId(client.id);
    try {
      const newSecret = await rotateApiClientSecret(client.id);
      setClients((cur) => cur.map((c) => (c.id === client.id ? { ...c, secretRotatedAt: new Date().toISOString() } : c)));
      setSecret({
        title: t('developer.client_secret_title', { defaultValue: 'Client secret — {{name}}', name: client.name }),
        description: t('developer.rotated_secret_desc', {
          defaultValue: 'The previous secret keeps working for 24 hours, then it is revoked.',
        }),
        value: newSecret,
      });
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.rotate_failed', { defaultValue: 'Could not rotate the secret.' }))));
    } finally {
      setBusyId(null);
    }
  };

  const onToggleStatus = async (client: ApiClient) => {
    const enable = client.status !== 'active';
    const ok = await confirm({
      title: enable
        ? t('developer.enable_title', { defaultValue: 'Enable API client' })
        : t('developer.disable_title', { defaultValue: 'Disable API client' }),
      message: enable
        ? t('developer.enable_confirm', { defaultValue: 'Re-enable “{{name}}”? Its credentials start working again immediately.', name: client.name })
        : t('developer.disable_confirm', { defaultValue: 'Disable “{{name}}”? All API calls with its credentials are rejected immediately.', name: client.name }),
      confirmLabel: enable
        ? t('developer.enable', { defaultValue: 'Enable' })
        : t('developer.disable', { defaultValue: 'Disable' }),
      danger: !enable,
    });
    if (!ok) return;
    setBusyId(client.id);
    try {
      await setApiClientEnabled(client.id, enable);
      setClients((cur) => cur.map((c) => (c.id === client.id ? { ...c, status: enable ? 'active' : 'disabled' } : c)));
      toast.success(enable
        ? t('developer.enabled', { defaultValue: 'Client enabled.' })
        : t('developer.disabled', { defaultValue: 'Client disabled.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.status_failed', { defaultValue: 'Could not change the client status.' }))));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('developer.loading_clients', { defaultValue: 'Loading API clients…' })}
      </div>
    );
  }
  if (unavailableMsg !== null) return <TemporarilyUnavailableState message={unavailableMsg} />;
  if (notEnabledMsg !== null) {
    return <FeatureNotEnabledState message={notEnabledMsg} />;
  }
  if (rolloutPending) {
    return (
      <RolloutPendingState
        message={t('developer.clients_rollout', { defaultValue: 'API client management is being rolled out to your workspace. Check back soon.' })}
      />
    );
  }
  if (loadError) {
    return (
      <div className="rounded-lg px-3 py-2 text-sm border bg-danger-soft border-danger text-danger-strong">
        {t('developer.clients_load_error', { defaultValue: 'Could not load API clients.' })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('developer.clients_subtitle', {
            defaultValue: 'OAuth clients for the KaziPeople API. Each client exchanges its ID + secret for short-lived access tokens limited to the scopes you grant.',
          })}
        </p>
        <Button onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4 mr-1" /> {t('developer.new_client', { defaultValue: 'New API client' })}
        </Button>
      </div>

      {clients.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-14 text-center text-muted-foreground">
            <KeyRound className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">
              {t('developer.no_clients', { defaultValue: 'No API clients yet. Create one to start calling the KaziPeople API.' })}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('developer.col_name', { defaultValue: 'Name' })}</TableHead>
                  <TableHead>{t('developer.col_client_id', { defaultValue: 'Client ID' })}</TableHead>
                  <TableHead>{t('developer.col_env', { defaultValue: 'Environment' })}</TableHead>
                  <TableHead>{t('developer.col_scopes', { defaultValue: 'Scopes' })}</TableHead>
                  <TableHead>{t('developer.col_status', { defaultValue: 'Status' })}</TableHead>
                  <TableHead>{t('developer.col_last_used', { defaultValue: 'Last used' })}</TableHead>
                  <TableHead className="text-right">{t('developer.col_actions', { defaultValue: 'Actions' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <p className="text-sm font-medium text-foreground">{c.name}</p>
                      {c.description && <TruncatedText as="p" className="text-xs text-muted-foreground max-w-[220px]">{c.description}</TruncatedText>}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1">
                        <code className="text-xs font-mono text-foreground">{c.clientId}</code>
                        <CopyButton text={c.clientId} label={t('developer.copy_client_id', { defaultValue: 'Copy client ID' })} />
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`border ${c.environment === 'sandbox'
                        ? 'bg-muted text-muted-foreground border-border'
                        : 'bg-green-100 dark:bg-green-500/[0.12] text-green-800 dark:text-green-300 border-green-200 dark:border-green-500/25'}`}>
                        {c.environment === 'sandbox'
                          ? t('developer.env_sandbox', { defaultValue: 'Sandbox' })
                          : t('developer.env_live', { defaultValue: 'Live' })}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[240px]">
                        {(c.scopes || []).map((s) => (
                          <span key={s} className="text-[11px] font-mono text-foreground bg-muted border border-border rounded-full px-2 py-0.5">
                            {s}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`border ${c.status === 'active'
                        ? 'bg-success-soft text-success-strong border-transparent'
                        : 'bg-danger-soft text-danger-strong border-transparent'}`}>
                        {c.status === 'active'
                          ? t('developer.status_active', { defaultValue: 'Active' })
                          : t('developer.status_disabled', { defaultValue: 'Disabled' })}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {c.lastUsedAt
                        ? fmtDateTime(c.lastUsedAt)
                        : t('developer.never_used', { defaultValue: 'Never' })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditing(c)} disabled={busyId === c.id}
                          aria-label={t('developer.edit_scopes', { defaultValue: 'Edit scopes' })} title={t('developer.edit_scopes', { defaultValue: 'Edit scopes' })}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onRotate(c)} disabled={busyId === c.id}
                          aria-label={t('developer.rotate', { defaultValue: 'Rotate secret' })} title={t('developer.rotate', { defaultValue: 'Rotate secret' })}>
                          <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost"
                          className={`h-8 w-8 p-0 ${c.status === 'active' ? 'text-danger hover:bg-red-50 dark:hover:bg-red-500/10' : 'text-success hover:bg-green-50 dark:hover:bg-green-500/10'}`}
                          onClick={() => onToggleStatus(c)} disabled={busyId === c.id}
                          aria-label={c.status === 'active'
                            ? t('developer.disable', { defaultValue: 'Disable' })
                            : t('developer.enable', { defaultValue: 'Enable' })}
                          title={c.status === 'active'
                            ? t('developer.disable', { defaultValue: 'Disable' })
                            : t('developer.enable', { defaultValue: 'Enable' })}>
                          <Power className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {creating && (
        <CreateClientDialog scopes={scopes} sensitiveSet={sensitiveSet} onCreated={onCreated} onClose={() => setCreating(false)} />
      )}
      {editing && (
        <EditScopesDialog
          client={editing}
          scopes={scopes}
          sensitiveSet={sensitiveSet}
          onSaved={(newScopes) => {
            setClients((cur) => cur.map((c) => (c.id === editing.id ? { ...c, scopes: newScopes } : c)));
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {secret && (
        <SecretModal
          title={secret.title}
          description={secret.description}
          secret={secret.value}
          onClose={() => setSecret(null)}
        />
      )}
    </div>
  );
};
